// web/src/marketing/Seo.tsx
//
// Per-route SEO/social tags for the marketing build. Mount one <Seo />
// per page; later mounts override earlier ones via react-helmet-async.
//
// Defaults (og:type, twitter:card, site OG image) come from
// index.marketing.html so this component only emits the per-page diff.
import { Helmet } from "react-helmet-async";

const APEX = "https://tapelens.com";

interface Props {
  /** Required. Used for <title> and og:title. ~50-60 chars. */
  title: string;
  /** Required. Used for <meta description> and og:description. ~150 chars. */
  description: string;
  /** Absolute path on the apex, e.g. "/pricing". Used to build canonical + og:url. */
  path: string;
  /**
   * Override the OG image. Default is the site-wide OG image set in
   * index.marketing.html (apex/og-image.webp).
   */
  image?: string;
  /** Block search-engine indexing for this specific page. */
  noindex?: boolean;
}

export function Seo({ title, description, path, image, noindex }: Props) {
  const url = `${APEX}${path.startsWith("/") ? path : `/${path}`}`;
  const imageUrl = image ?? `${APEX}/og-image.webp`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />

      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {noindex && <meta name="robots" content="noindex,nofollow" />}
    </Helmet>
  );
}
